import { createFileRoute, Link, redirect, useSearch } from "@tanstack/react-router";
import { createServerFn, useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { supabaseAdmin } from "@/integrations/supabase/client.server";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Fuel, Calendar, Gauge, Settings, Car, Palette, Hash, ChevronLeft, ChevronRight, Phone, X, ZoomIn, CalendarCheck } from "lucide-react";
import { useState, useEffect, useCallback, useRef } from "react";
import { CONTACT } from "@/config/contact";
import { buildBreadcrumbJsonLd } from "@/lib/breadcrumb-jsonld";
import { CarInquiryForm } from "@/components/CarInquiryForm";
import { TestDriveDialog } from "@/components/TestDriveDialog";
import { OpeningStatus } from "@/components/OpeningStatus";
import { trackConversion } from "@/lib/pixel";
import { trackCarViewContent } from "@/lib/view-content.functions";
import { getGaClientId } from "@/lib/ga-client";
import { getAttributionForEvent } from "@/lib/attribution";
import { buildSeoMeta, clampDescription } from "@/lib/seo";
import { buildVehicleJsonLd } from "@/lib/jsonld";
import { CarJsonLdDebug } from "@/components/CarJsonLdDebug";
import { LISTING_FROM_PARAM, decodeListingFrom } from "@/lib/listing-back-link";
import { dispatchChatFabHide } from "@/lib/chat-fab-events";
import { fetchCarManifest, type CarManifest } from "@/lib/car-image-manifest";
import { CarPicture } from "@/components/CarPicture";
import { buildCarFaq, buildCarFaqJsonLd } from "@/lib/car-faq";
import { resolveCarSlug } from "@/lib/car-slug";

// Server-fn used by the loader to fetch SEO-relevant fields. Runs on both
// client and server (TanStack loaders are isomorphic), but bypasses RLS via
// the admin client so SSR sees the same row anonymous visitors see.
const fetchCarMeta = createServerFn({ method: "GET" })
  .inputValidator((input: unknown) => {
    const v = (input as { slug?: string })?.slug;
    if (typeof v !== "string" || !v) throw new Error("slug required");
    return { slug: v };
  })
  .handler(async ({ data }) => {
    const fields =
      "id, brand, model, variant, year, mileage, price, fuel_type, transmission, color, body_type, doors, seats, horsepower, engine_size, description, images, status, is_leasing, leasing_price, seo_title, seo_description, seo_slug";
    let { data: row } = await supabaseAdmin
      .from("cars")
      .select(fields)
      .eq("seo_slug", data.slug)
      .maybeSingle();
    if (!row) {
      const r = await supabaseAdmin.from("cars").select(fields).eq("id", data.slug).maybeSingle();
      row = r.data;
    }
    return row;
  });

// UUID v4 shape used by Supabase `cars.id`. We only force a redirect when the
// URL param matches this exact shape AND the resolved row has a slug — that
// way:
//   - Legacy `/biler-til-salg/<uuid>` deep links (older marketing emails, FB
//     posts before slugs existed, internal CRM links) get a permanent 301
//     to the canonical `/biler-til-salg/<slug>` URL, consolidating PageRank
//     and stopping Google from crawling/indexing both variants.
//   - Cars without a slug yet (admin hasn't filled it in) keep working at
//     the UUID URL with no redirect loop.
//   - Genuinely-mistyped slugs that happen to look UUID-shaped never match
//     a real row and fall through to the normal not-found handling.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const Route = createFileRoute("/biler-til-salg/$slug")({
  loader: async ({ params }) => {
    try {
      const car = await fetchCarMeta({ data: { slug: params.slug } });
      // Server-side 301 to the slug-canonical URL when the visitor landed
      // on the UUID variant of a car that has a slug. Throwing `redirect`
      // from a loader is the TanStack Start idiom — on the server it emits
      // a real 301 response (consolidating crawl signals); on client-side
      // navigation it replaces the history entry, so users never get stuck
      // on the legacy URL either.
      // Brug resolveCarSlug så whitespace-only seo_slug behandles som tom
      // (samme regel som sitemap-generatoren), og vi aldrig redirecter til
      // "/biler-til-salg/   ".
      const canonicalSlug = car ? resolveCarSlug(car) : null;
      if (
        car &&
        canonicalSlug &&
        canonicalSlug !== params.slug &&
        UUID_RE.test(params.slug)
      ) {
        throw redirect({
          to: "/biler-til-salg/$slug",
          params: { slug: canonicalSlug },
          statusCode: 301,
        });
      }
      return { car };
    } catch (err) {
      // Re-throw redirect (and any other framework signal) — only swallow
      // genuine fetch errors so the not-found UI still renders for missing
      // cars without breaking the redirect path.
      if (err && typeof err === "object" && ("isRedirect" in err || "to" in err)) {
        throw err;
      }
      return { car: null };
    }
  },
  head: ({ loaderData, params }) => {
    const car = loaderData?.car;
    const slug = params.slug;
    if (!car) {
      return buildSeoMeta({
        title: "Bil ikke fundet · AutoXChange",
        description: "Den bil findes ikke længere — se vores aktuelle udvalg.",
        path: `/biler-til-salg/${slug}`,
        noIndex: true,
        // Removed cars: stop Google from serving a cached snapshot of the
        // old listing alongside "noindex" so outdated price/availability
        // doesn't surface via the cache: operator.
        noArchive: true,
      });
    }
    // Sold/inactive cars: keep the page reachable (so deep links from search
    // results, bookmarks, or marketing emails still resolve) but signal
    // noindex,nofollow so Google drops them from the index instead of
    // serving stale "for sale" snippets that frustrate buyers.
    const isSold = !!car.status && car.status !== "active";
    const name = `${car.brand} ${car.model}${car.variant ? " " + car.variant : ""}`;
    const title =
      car.seo_title ||
      `${name} ${car.year} · ${new Intl.NumberFormat("da-DK").format(car.price)} kr · AutoXChange`;
    const description = clampDescription(
      car.seo_description ||
        car.description ||
        `${name} fra ${car.year} med ${new Intl.NumberFormat("da-DK").format(car.mileage)} km. Pris ${new Intl.NumberFormat("da-DK").format(car.price)} kr. Se billeder, udstyr og book prøvetur hos AutoXChange i Børkop.`,
    );
    // OG-image: brug 1200x630 (Facebook anbefaling) — Bilinfo CDN serverer
    // den størrelse via ?class= suffix. Eget storage-billede har allerede
    // korrekt størrelse, så vi rører ikke ved det.
    const rawImage = car.images && car.images.length > 0 ? car.images[0] : undefined;
    const image = rawImage
      ? rawImage.includes("/storage/v1/")
        ? rawImage
        : `${rawImage.replace(/\?class=S\d+X\d+$/i, "")}?class=S1200X630`
      : undefined;
    const seo = buildSeoMeta({
      title: isSold ? `${name} ${car.year} · SOLGT · AutoXChange` : title,
      description: isSold
        ? `${name} fra ${car.year} er solgt. Se vores aktuelle udvalg af brugte biler hos AutoXChange i Børkop.`
        : description,
      path: `/biler-til-salg/${resolveCarSlug(car)}`,
      image,
      // Beskrivende alt-tekst pr. bil (mærke + model + variant + årgang) —
      // bruges som og:image:alt / twitter:image:alt for både a11y og bedre
      // social-preview-signal end et generisk fallback.
      imageAlt: image
        ? `${name} ${car.year} — ${isSold ? "solgt hos AutoXChange i Børkop" : "til salg hos AutoXChange i Børkop"}`
        : undefined,
      ogType: "product",
      noIndex: isSold,
      // Sold cars: pair noindex with noarchive so any cached snapshot
      // Google may still hold gets dropped — prevents stale "for sale"
      // pricing from leaking through cache: lookups after the car is gone.
      noArchive: isSold,
    });
    return {
      ...seo,
      scripts: [
        {
          type: "application/ld+json",
          children: buildVehicleJsonLd({
            id: car.id,
            slug: resolveCarSlug(car),
            brand: car.brand,
            model: car.model,
            variant: car.variant,
            year: car.year,
            mileage: car.mileage,
            price: car.price,
            fuelType: car.fuel_type,
            transmission: car.transmission,
            color: car.color,
            bodyType: car.body_type,
            doors: car.doors,
            seats: car.seats,
            horsepower: car.horsepower,
            engineSize: car.engine_size,
            description: car.description,
            images: car.images,
            status: car.status,
            isLeasing: car.is_leasing,
            leasingPrice: car.leasing_price,
          }),
        },
        // FAQPage JSON-LD — generated from the same car spec data that
        // the visible "Ofte stillede spørgsmål" section renders below.
        // Keeping both fed from `buildCarFaq()` guarantees the structured
        // data mirrors visible content (a Google policy requirement).
        {
          type: "application/ld+json",
          children: buildCarFaqJsonLd({
            brand: car.brand,
            model: car.model,
            variant: car.variant,
            year: car.year,
            mileage: car.mileage,
            price: car.price,
            fuel_type: car.fuel_type,
            transmission: car.transmission,
            horsepower: car.horsepower,
            body_type: car.body_type,
            is_leasing: car.is_leasing,
            leasing_price: car.leasing_price,
          }),
        },
      ],
    };
  },
  component: CarDetail,
});

function formatPrice(price: number) {
  return new Intl.NumberFormat("da-DK").format(price);
}

function fuelLabel(fuel: string | null) {
  if (!fuel) return "-";
  const map: Record<string, string> = {
    benzin: "Benzin", diesel: "Diesel", el: "El", hybrid: "Hybrid", plugin_hybrid: "Plugin Hybrid",
  };
  return map[fuel] || fuel;
}

function CarDetail() {
  const { slug } = Route.useParams();
  const trackViewContent = useServerFn(trackCarViewContent);
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [testDriveOpen, setTestDriveOpen] = useState(false);
  // Guards against double-firing under React StrictMode and re-renders.
  const viewedCarIdRef = useRef<string | null>(null);
  // Refs for swipe gesture + auto-scrolling the active thumbnail into view.
  const touchStartXRef = useRef<number | null>(null);
  const touchStartYRef = useRef<number | null>(null);
  const thumbStripRef = useRef<HTMLDivElement | null>(null);
  const thumbBtnRefs = useRef<Array<HTMLButtonElement | null>>([]);
  // Tracks the "Ring til os" CTA in the sidebar; when it scrolls into view on
  // mobile we tell the global ChatWidget to hide so the FAB doesn't visually
  // compete with the phone link sitting in the same corner of the screen.
  const callZoneRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = callZoneRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([entry]) => dispatchChatFabHide(entry.isIntersecting),
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      // Always re-show on unmount so the FAB returns when navigating away.
      dispatchChatFabHide(false);
    };
  }, []);

  // Restore the listing's filter/sort state on the back-link if the user
  // arrived from /biler-til-salg with a `?from=` token. We read loosely
  // because this route doesn't define a validateSearch schema.
  const search = useSearch({ strict: false }) as { [LISTING_FROM_PARAM]?: string };
  const restoredFrom = decodeListingFrom(search?.[LISTING_FROM_PARAM]);
  const backToListingHref = restoredFrom
    ? `/biler-til-salg?${restoredFrom}`
    : "/biler-til-salg";

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);

  const lightboxPrev = useCallback((images: string[]) => {
    setLightboxIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, []);

  const lightboxNext = useCallback((images: string[]) => {
    setLightboxIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, []);

  // Mobile swipe gesture for the main image. Only horizontal moves >40px
  // count as swipes — anything more vertical is treated as scroll so the
  // page never feels "trapped" inside the gallery.
  const onTouchStart = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
    const t = e.touches[0];
    touchStartXRef.current = t.clientX;
    touchStartYRef.current = t.clientY;
  }, []);

  const onTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>, imagesLen: number) => {
      const startX = touchStartXRef.current;
      const startY = touchStartYRef.current;
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      if (startX === null || startY === null || imagesLen < 2) return;
      const t = e.changedTouches[0];
      const dx = t.clientX - startX;
      const dy = t.clientY - startY;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      if (dx < 0) {
        setCurrentImage((p) => (p === imagesLen - 1 ? 0 : p + 1));
      } else {
        setCurrentImage((p) => (p === 0 ? imagesLen - 1 : p - 1));
      }
    },
    [],
  );

  // Keep the active thumbnail visible whenever currentImage changes — works
  // for swipes, arrow buttons, and lightbox sync alike.
  useEffect(() => {
    const btn = thumbBtnRefs.current[currentImage];
    if (btn) {
      btn.scrollIntoView({ behavior: "smooth", inline: "nearest", block: "nearest" });
    }
  }, [currentImage]);

  // Seed the query with the SSR loader's `car` so the first client render
  // already has data — no skeleton flash on hydration. Without this, breakpoint
  // screenshot tooling captures the loading placeholder instead of the real
  // layout because `useQuery` defaults to `isLoading: true` until the client
  // queryFn resolves, even when SSR rendered the full page.
  const { car: initialCar } = Route.useLoaderData();
  const queryFn = async () => {
    // Try by slug first, then by id
    let { data } = await supabase
      .from("cars")
      .select("id, brand, model, variant, year, mileage, price, fuel_type, transmission, color, body_type, doors, seats, engine_size, horsepower, description, equipment, images, status, is_new, is_popular, is_leasing, leasing_price, first_registration, seo_title, seo_description, seo_slug, created_at")
      .eq("seo_slug", slug)
      .single();

    if (!data) {
      const result = await supabase
        .from("cars")
        .select("id, brand, model, variant, year, mileage, price, fuel_type, transmission, color, body_type, doors, seats, engine_size, horsepower, description, equipment, images, status, is_new, is_popular, is_leasing, leasing_price, first_registration, seo_title, seo_description, seo_slug, created_at")
        .eq("id", slug)
        .single();
      data = result.data;
    }
    return data;
  };
  type CarRow = Awaited<ReturnType<typeof queryFn>>;
  const { data: car, isLoading } = useQuery<CarRow>({
    queryKey: ["car", slug],
    queryFn,
    // Loader fetches a SEO-focused subset (no `equipment`/flags) — fill the
    // missing fields with null so the equipment/badge UI hits its null-safe
    // branches until the queryFn refetches the complete row. This keeps
    // `isLoading` false on first render so screenshots capture the real layout.
    initialData: initialCar
      ? ({ ...initialCar, equipment: null, is_new: null, is_popular: null, first_registration: null, created_at: null } as unknown as CarRow)
      : undefined,
  });

  // Multi-format derivative manifest for the gallery. Cars uploaded
  // through the new admin pipeline get AVIF/WebP/JPG @ 960/1280/1920 per
  // image; legacy cars (Bilinfo CDN, pre-pipeline uploads) won't have
  // any rows and the gallery falls back to the existing single <img>.
  const { data: imageManifest } = useQuery<CarManifest>({
    queryKey: ["car", "manifest", car?.id ?? slug],
    enabled: !!car?.id,
    queryFn: () => fetchCarManifest(car!.id),
    staleTime: 5 * 60 * 1000,
  });

  // Conversion event: ViewContent (Meta + CAPI) + view_item (GA4 + MP) — fires
  // once per loaded car. The server returns an event_id we reuse on the browser
  // pixel/gtag for deduplication. Server side runs even without marketing
  // consent (no PII sent — only IP/UA from the request) which expands Meta's
  // retargeting pool. Browser side is a no-op without consent.
  useEffect(() => {
    if (!car) return;
    if (viewedCarIdRef.current === car.id) return;
    viewedCarIdRef.current = car.id;

    const name = `${car.brand} ${car.model}${car.variant ? " " + car.variant : ""}`;
    const sourceUrl =
      typeof window !== "undefined" ? window.location.href : undefined;
    const gaClientId = getGaClientId() ?? undefined;

    (async () => {
      let eventId: string | undefined;
      try {
        const res = await trackViewContent({
          data: {
            carId: car.id,
            carName: name,
            price: car.price,
            sourceUrl,
            gaClientId,
            attribution: getAttributionForEvent(),
          },
        });
        eventId = res?.eventId;
      } catch (e) {
        console.error("ViewContent server fn failed:", e);
      }

      trackConversion({
        meta: {
          event: "ViewContent",
          params: {
            content_type: "vehicle",
            content_ids: [car.id],
            content_name: name,
            content_category: "car",
            value: car.price,
            currency: "DKK",
          },
          eventId,
        },
        ga: {
          event: "view_item",
          params: {
            event_id: eventId,
            currency: "DKK",
            value: car.price,
            items: [
              {
                item_id: car.id,
                item_name: name,
                item_brand: car.brand,
                item_category: "car",
                price: car.price,
              },
            ],
          },
        },
      });
    })();
  }, [car, trackViewContent]);

  if (isLoading) {
    return (
      <div className="pt-24">
        <div className="mx-auto max-w-7xl px-6 py-12">
          <div className="h-96 animate-pulse rounded-lg bg-muted" />
        </div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="flex min-h-screen items-center justify-center pt-24">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Bilen blev ikke fundet</h1>
          <a href={backToListingHref} className="mt-4 inline-flex items-center gap-2 text-accent hover:underline">
            <ArrowLeft size={16} /> Tilbage til biler
          </a>
        </div>
      </div>
    );
  }

  const hasImages = !!car.images?.length;
  const images = hasImages ? car.images! : [];
  const carName = `${car.brand} ${car.model}${car.variant ? " " + car.variant : ""}`;
  const carFullName = `${carName} ${car.year}`;
  const isSold = car.status === "sold";

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: "Forside", path: "" },
    { name: "Biler til salg", path: "biler-til-salg" },
    { name: `${car.brand} ${car.model}`, path: `biler-til-salg/${resolveCarSlug(car)}` },
  ]);

  return (
    <div className="pt-20 lg:pt-24">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJsonLd }} />
      {/* Bottom padding reserves space for the mobile sticky action bar so
          the page footer/last form field is never hidden behind it. */}
      <div className="mx-auto max-w-7xl px-4 py-5 pb-28 sm:px-6 sm:py-8 md:pb-8">
        {/* Back + Breadcrumb row — compact on mobile, single line where possible */}
        <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 sm:mb-6 sm:gap-4">
          <a
            href={backToListingHref}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground sm:gap-2 sm:px-4 sm:py-2 sm:text-sm"
          >
            <ArrowLeft size={14} className="sm:h-4 sm:w-4" /> Tilbage
          </a>
          <nav className="flex items-center gap-1.5 text-xs text-muted-foreground sm:gap-2 sm:text-sm" aria-label="Brødkrumme">
            <Link to="/" className="hover:text-accent">Forside</Link>
            <span>/</span>
            <Link to="/biler-til-salg" className="hover:text-accent">Biler til salg</Link>
            <span>/</span>
            <span className="truncate text-foreground">{car.brand} {car.model}</span>
          </nav>
        </div>

        <div className="grid gap-6 sm:gap-8 lg:grid-cols-3">
          {/* Images — `min-w-0` so the gallery never forces the grid track
              wider than the viewport on narrow screens. */}
          <div className="min-w-0 lg:col-span-2">
            {hasImages ? (
              <>
                <div
                  className="group relative aspect-[4/3] cursor-pointer touch-pan-y select-none overflow-hidden rounded-lg bg-muted [contain:layout_paint] sm:aspect-[16/10]"
                  onClick={() => openLightbox(currentImage)}
                  onTouchStart={onTouchStart}
                  onTouchEnd={(e) => onTouchEnd(e, images.length)}
                >
                  <CarPicture
                    src={images[currentImage]}
                    manifest={imageManifest?.[images[currentImage]]}
                    alt={`${carFullName} – billede ${currentImage + 1} af ${images.length}`}
                    width={1600}
                    height={1000}
                    sizes="(max-width: 1024px) 100vw, 66vw"
                    priority
                    fallbackBaseWidth={1280}
                    fallbackQuality={82}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    pictureClassName="block h-full w-full"
                  />
                  {/* Preload neighboring images so left/right swipes feel
                      instant on mobile. Hidden from a11y and layout. */}
                  {images.length > 1 && (
                    <div aria-hidden="true" className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0">
                      <img
                        src={images[(currentImage + 1) % images.length]}
                        alt=""
                        loading="eager"
                        decoding="async"
                        fetchPriority="low"
                      />
                      <img
                        src={images[(currentImage - 1 + images.length) % images.length]}
                        alt=""
                        loading="eager"
                        decoding="async"
                        fetchPriority="low"
                      />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-foreground/0 transition-colors group-hover:bg-foreground/20">
                    <ZoomIn className="h-8 w-8 text-white opacity-0 transition-opacity group-hover:opacity-100" />
                  </div>
                  {images.length > 1 && (
                    <>
                      <button
                        aria-label="Forrige billede"
                        onClick={(e) => { e.stopPropagation(); setCurrentImage((prev) => (prev === 0 ? images.length - 1 : prev - 1)); }}
                        className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-foreground/50 p-2 text-primary-foreground hover:bg-foreground/70 sm:left-3"
                      >
                        <ChevronLeft size={20} />
                      </button>
                      <button
                        aria-label="Næste billede"
                        onClick={(e) => { e.stopPropagation(); setCurrentImage((prev) => (prev === images.length - 1 ? 0 : prev + 1)); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-foreground/50 p-2 text-primary-foreground hover:bg-foreground/70 sm:right-3"
                      >
                        <ChevronRight size={20} />
                      </button>
                    </>
                  )}
                  {isSold && (
                    <span className="absolute top-3 left-3 z-20 rounded-md bg-red-500 px-3 py-1 text-sm font-bold text-white sm:top-4 sm:left-4 sm:px-4 sm:py-1.5 sm:text-base">
                      SOLGT
                    </span>
                  )}
                  <span className="absolute bottom-2 right-2 rounded-full bg-foreground/60 px-2.5 py-0.5 text-[11px] text-white sm:bottom-3 sm:right-3 sm:px-3 sm:py-1 sm:text-xs">
                    {currentImage + 1} / {images.length}
                  </span>
                </div>

                {images.length > 1 && (
                  <div
                    ref={thumbStripRef}
                    className="mt-3 flex h-14 gap-2 overflow-x-auto overscroll-x-contain scroll-smooth [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden sm:mt-4 sm:h-16"
                  >
                    {images.map((img, i) => (
                      <button
                        key={i}
                        ref={(el) => { thumbBtnRefs.current[i] = el; }}
                        type="button"
                        onClick={() => setCurrentImage(i)}
                        aria-label={`Vælg billede ${i + 1}`}
                        aria-current={i === currentImage ? "true" : undefined}
                        className={`h-14 w-20 flex-shrink-0 overflow-hidden rounded-md border-2 transition-[border-color,opacity] duration-150 sm:h-16 sm:w-24 ${i === currentImage ? "border-accent opacity-100" : "border-transparent opacity-60 hover:opacity-100"}`}
                      >
                      <img
                        src={img}
                        alt={`${carFullName} miniature ${i + 1}`}
                        width="96"
                        height="64"
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover"
                      />
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="relative flex aspect-[4/3] flex-col items-center justify-center gap-4 overflow-hidden rounded-lg bg-gradient-to-br from-muted via-muted to-muted/60 sm:aspect-[16/10]">
                <div
                  aria-hidden="true"
                  className="absolute inset-0 text-accent opacity-[0.08]"
                  style={{
                    backgroundImage:
                      "repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 1px, transparent 14px)",
                  }}
                />
                <div className="relative z-10 flex h-20 w-20 items-center justify-center rounded-full bg-background/80 ring-1 ring-border">
                  <Car size={36} className="text-accent" strokeWidth={1.75} />
                </div>
                <div className="relative z-10 px-6 text-center">
                  <p className="text-lg font-semibold text-foreground">{car.brand} {car.model}</p>
                  <p className="mt-1 text-sm text-muted-foreground">Foto følger snart — kontakt os for at se bilen</p>
                </div>
              </div>
            )}

            {/* Specs grid — placed inside the gallery column on lg+ so it
                fills the natural gap below the photo while the sidebar
                (which is much taller due to the inquiry form) continues
                independently to the right. Without this, specs would sit
                far below the entire grid row, appearing "pinned" near the
                footer because the sidebar stretched the row. */}
            <div className="mt-6 grid grid-cols-2 gap-3 sm:mt-8 sm:gap-4">
              {[
                { icon: Gauge, label: "Kilometer", value: `${formatPrice(car.mileage)} km` },
                { icon: Calendar, label: "Modelår", value: String(car.year) },
                { icon: Fuel, label: "Brændstof", value: fuelLabel(car.fuel_type) },
                { icon: Settings, label: "Gearkasse", value: car.transmission === "automatik" ? "Automatik" : "Manuel" },
                { icon: Car, label: "Karosseri", value: car.body_type || "-" },
                { icon: Palette, label: "Farve", value: car.color || "-" },
                { icon: Hash, label: "Hestekræfter", value: car.horsepower ? `${car.horsepower} HK` : "-" },
                { icon: Hash, label: "Motor", value: car.engine_size || "-" },
              ].map((spec) => (
                <div key={spec.label} className="flex min-w-0 items-start gap-2.5 rounded-lg border border-border bg-card p-3 sm:gap-3 sm:p-4">
                  <spec.icon className="mt-0.5 h-4 w-4 shrink-0 text-accent sm:h-5 sm:w-5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] text-muted-foreground sm:text-xs">{spec.label}</p>
                    <p
                      className="text-sm font-medium leading-snug text-card-foreground [overflow-wrap:anywhere] [word-break:break-word] hyphens-auto sm:text-base"
                      title={spec.value}
                    >
                      {spec.value}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Description */}
            {car.description && (
              <div className="mt-6 rounded-lg border border-border bg-card p-4 sm:mt-8 sm:p-6">
                <h2 className="text-lg font-bold text-card-foreground sm:text-xl">Beskrivelse</h2>
                <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground sm:mt-4 sm:text-base">{car.description}</div>
              </div>
            )}

            {/* Equipment */}
            {car.equipment && car.equipment.length > 0 && (
              <div className="mt-6 rounded-lg border border-border bg-card p-4 sm:p-6">
                <h2 className="text-lg font-bold text-card-foreground sm:text-xl">Udstyr</h2>
                <div className="mt-3 grid gap-2 sm:mt-4 sm:grid-cols-2">
                  {car.equipment.map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <div className="h-1.5 w-1.5 rounded-full bg-accent" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ — generated from car spec data via `buildCarFaq()`.
                Same source feeds the FAQPage JSON-LD in head() so the
                visible answers always match the structured data. Native
                <details> gives keyboard + screen-reader expand/collapse
                without any JS. */}
            <section className="mt-6 rounded-lg border border-border bg-card p-4 sm:mt-8 sm:p-6" aria-labelledby="car-faq-heading">
              <h2 id="car-faq-heading" className="text-lg font-bold text-card-foreground sm:text-xl">
                Ofte stillede spørgsmål
              </h2>
              <div className="mt-3 divide-y divide-border sm:mt-4">
                {buildCarFaq({
                  brand: car.brand,
                  model: car.model,
                  variant: car.variant,
                  year: car.year,
                  mileage: car.mileage,
                  price: car.price,
                  fuel_type: car.fuel_type,
                  transmission: car.transmission,
                  horsepower: car.horsepower,
                  body_type: car.body_type,
                  is_leasing: car.is_leasing,
                  leasing_price: car.leasing_price,
                }).map((f, i) => (
                  <details
                    key={i}
                    className="group py-3 [&_summary::-webkit-details-marker]:hidden"
                    open={i === 0}
                  >
                    <summary className="flex cursor-pointer items-center justify-between gap-3 text-sm font-medium text-card-foreground sm:text-base">
                      <span>{f.question}</span>
                      <ChevronRight
                        size={18}
                        className="shrink-0 text-muted-foreground transition-transform group-open:rotate-90"
                        aria-hidden="true"
                      />
                    </summary>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
                      {f.answer}
                    </p>
                  </details>
                ))}
              </div>
            </section>
          </div>

          {/* Details sidebar — `min-w-0` prevents the inquiry form's
              long inputs/buttons from forcing horizontal page overflow. */}
          <div className="min-w-0">
            <div className="rounded-lg border border-border bg-card p-4 sm:p-6" data-v="2">
              {isSold && (
                <span className="mb-3 inline-block rounded-md bg-red-500 px-3 py-1 text-sm font-bold text-white">
                  SOLGT
                </span>
              )}
              <h1 className="text-xl font-bold text-card-foreground sm:text-2xl">
                {car.brand} {car.model} {car.year}
              </h1>
              {car.variant && <p className="mt-1 text-sm text-muted-foreground sm:text-base">{car.variant}</p>}

              <div className="mt-4 sm:mt-6">
                <span className="text-sm text-muted-foreground">Pris</span>
                {isSold ? (
                  <p className="text-2xl font-bold text-red-500 line-through sm:text-3xl">DKK {formatPrice(car.price)},-</p>
                ) : (
                  <p className="text-2xl font-bold text-card-foreground sm:text-3xl">DKK {formatPrice(car.price)},-</p>
                )}
              </div>

              {car.is_leasing && car.leasing_price && !isSold && (
                <p className="mt-2 text-sm text-muted-foreground">
                  Leasing fra <span className="font-semibold text-card-foreground">DKK {formatPrice(car.leasing_price)},-</span>/md
                </p>
              )}

              {isSold ? (
                <p className="mt-5 text-sm font-medium text-muted-foreground sm:mt-6">
                  Denne bil er desværre allerede solgt. Kontakt os gerne hvis du leder efter noget lignende.
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setTestDriveOpen(true)}
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-accent-foreground transition hover:brightness-110 sm:mt-6"
                >
                  <CalendarCheck size={16} /> Book prøvetur
                </button>
              )}

              <div className="mt-3" ref={callZoneRef}>
                <a
                  href={CONTACT.phoneHref}
                  className="flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium text-foreground transition hover:bg-muted"
                >
                  <Phone size={16} /> Ring til os: {CONTACT.phoneDisplay}
                </a>
              </div>
              <OpeningStatus className="mt-2 px-1" />

              <div className="mt-5 space-y-1 text-sm sm:mt-6">
                <p className="font-semibold text-card-foreground">{CONTACT.companyName}</p>
                <p className="text-muted-foreground">{CONTACT.addressLine}</p>
                <p className="text-muted-foreground">CVR: {CONTACT.cvr}</p>
              </div>
            </div>

            <div className="mt-6">
              <CarInquiryForm
                carId={car.id}
                carLabel={`${car.brand} ${car.model}${car.variant ? " " + car.variant : ""} (${car.year})`}
                showTimeSlots
              />
            </div>
          </div>
        </div>


        {/* Admin-only: per-car JSON-LD debug + Google Shopping field check.
            Public visitors see nothing — the component returns null when
            the viewer isn't staff. */}
        <CarJsonLdDebug
          carUrl={`https://${typeof window !== "undefined" ? window.location.host : "autoxchange.dk"}/biler-til-salg/${resolveCarSlug(car)}`}
        />
      </div>

      {/* Mobile sticky action bar — gives the two primary CTAs (call + book
          test drive) one-tap access from anywhere on the page without the
          user needing to scroll back to the price card. Hidden on >=lg
          where the sidebar is always visible alongside the gallery.
          `pb-[env(safe-area-inset-bottom)]` keeps the buttons clear of
          iOS Home Indicator. */}
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-2.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur supports-[backdrop-filter]:bg-card/85 xs:px-3 md:hidden">
        {isSold ? (
          <div className="mx-auto flex max-w-7xl items-center justify-between gap-2 py-1">
            <span className="rounded-md bg-red-500 px-3 py-1.5 text-sm font-bold text-white">
              SOLGT
            </span>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-foreground transition hover:bg-muted"
            >
              <Phone size={16} /> Ring til os
            </a>
          </div>
        ) : (
          <div className="mx-auto flex max-w-7xl items-center gap-1 xs:gap-1.5 sm:gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-[10px] leading-none text-muted-foreground xs:text-[11px]">Pris</p>
              {/* Use shorter "kr" suffix instead of "DKK" prefix on narrow
                  screens (<380px) so 6-digit prices fit without truncation.
                  `whitespace-nowrap` guards against the price wrapping onto a
                  second line if buttons grow (e.g. translations). */}
              <p className="mt-0.5 truncate whitespace-nowrap text-sm font-bold leading-tight text-card-foreground sm:text-base">
                <span className="hidden xs:inline">DKK </span>
                {formatPrice(car.price)}
                <span className="xs:hidden"> kr</span>
                <span className="hidden xs:inline">,-</span>
              </p>
            </div>
            <a
              href={CONTACT.phoneHref}
              aria-label={`Ring til ${CONTACT.phoneDisplay}`}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border bg-background text-foreground transition hover:bg-muted xs:h-11 xs:w-11"
            >
              <Phone size={18} />
            </a>
            <button
              type="button"
              onClick={() => setTestDriveOpen(true)}
              className="inline-flex h-10 shrink-0 items-center justify-center gap-1 whitespace-nowrap rounded-md bg-accent px-2.5 text-xs font-semibold text-accent-foreground transition hover:brightness-110 xs:h-11 xs:gap-1.5 xs:px-3 xs:text-sm sm:px-4"
            >
              <CalendarCheck size={15} />
              <span className="xs:hidden">Prøvetur</span>
              <span className="hidden xs:inline">Book prøvetur</span>
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={() => lightboxPrev(images)}
          onNext={() => lightboxNext(images)}
          onSelect={setLightboxIndex}
        />
      )}

      <TestDriveDialog
        open={testDriveOpen}
        onOpenChange={setTestDriveOpen}
        carId={car.id}
        carLabel={`${car.brand} ${car.model}${car.variant ? " " + car.variant : ""} (${car.year})`}
      />
    </div>
  );
}

function Lightbox({
  images,
  currentIndex,
  onClose,
  onPrev,
  onNext,
  onSelect,
}: {
  images: string[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSelect: (i: number) => void;
}) {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black/95" onClick={onClose}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-sm text-white/70">{currentIndex + 1} / {images.length}</span>
        <button onClick={onClose} aria-label="Luk billedvisning" className="rounded-full p-2 text-white/90 transition hover:bg-white/10 hover:text-white">
          <X size={24} aria-hidden="true" />
        </button>
      </div>

      {/* Main image */}
      <div className="relative flex flex-1 items-center justify-center px-4" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[currentIndex]}
          alt=""
          decoding="async"
          fetchPriority="high"
          className="max-h-[75vh] max-w-full rounded object-contain"
        />
        {/* Preload prev/next so arrow-key navigation has no flash. */}
        {images.length > 1 && (
          <div aria-hidden="true" className="pointer-events-none absolute h-0 w-0 overflow-hidden opacity-0">
            <img
              src={images[(currentIndex + 1) % images.length]}
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="low"
            />
            <img
              src={images[(currentIndex - 1 + images.length) % images.length]}
              alt=""
              loading="eager"
              decoding="async"
              fetchPriority="low"
            />
          </div>
        )}
        {images.length > 1 && (
          <>
            <button
              onClick={onPrev}
              aria-label="Forrige billede"
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-3 text-white transition hover:bg-white/30"
            >
              <ChevronLeft size={24} aria-hidden="true" />
            </button>
            <button
              onClick={onNext}
              aria-label="Næste billede"
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/20 p-3 text-white transition hover:bg-white/30"
            >
              <ChevronRight size={24} aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex justify-center gap-2 overflow-x-auto px-4 py-4" onClick={(e) => e.stopPropagation()}>
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => onSelect(i)}
              aria-label={`Vis billede ${i + 1}`}
              aria-current={i === currentIndex ? "true" : undefined}
              className={`h-14 w-20 flex-shrink-0 overflow-hidden rounded border-2 transition-all ${i === currentIndex ? "border-accent" : "border-transparent opacity-70 hover:opacity-100"}`}
            >
              <img src={img} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
